import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Checkbox, IconCheck, Link, Modal,
  Skeleton,
} from 'gatorcomponents';
import useLocale from '@/hooks/useLocale';
import withErrorBoundary from '@/utils/errorBoundary';
import snappyMonarxPath from '../../media/snappy-monarx.svg';
import * as Styles from './ActiveTrialModal.styles';
import { SERVICE_TERMS } from '@/config/urls/supportUrls';

const DEFAULT_TEST_ID = 'gator-protect-protection-modal';

const ActiveTrialModal = ({
  open,
  onClose,
  onActivate,
  selectedPlan,
  isTrialActive,
  isTrialExpired,
  isAAClient,
  monthly,
  loadingData = false,
  fastCheckoutLoading,
  testId = DEFAULT_TEST_ID,
}) => {
  const { gatorProtect: gatorProtectLocale } = useLocale();
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!open) {
      setAgreed(false);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const modalLocale = gatorProtectLocale?.activeTrialModal;

  if (!modalLocale) {
    return null;
  }

  const getScenario = () => {
    if (isTrialActive) {
      return { ...modalLocale.trialActive };
    }

    if (isTrialExpired) {
      return modalLocale.trialActive;
    }

    return {
      ...modalLocale.trialActivation,
      title: modalLocale.trialActivation.title(isAAClient),
      subtitle: modalLocale.trialActivation.subtitle(monthly?.formattedPrice ?? '...'),
    };
  };

  const scenario = getScenario();
  const parsedMessage = typeof scenario.message === 'function'
    ? scenario.message((selectedPlan && selectedPlan.expire_days) || 0)
    : scenario.message;

  const checklistItems = scenario.checklistItems || modalLocale.checklistItems || [];

  const rawButton = scenario.buttonLabel;
  const buttonLabel = typeof rawButton === 'function'
    ? rawButton(monthly?.formattedPrice ?? '...')
    : rawButton;

  const content = {
    title: scenario.title,
    subtitle: scenario.subtitle,
    ...(parsedMessage ? { message: parsedMessage } : {}),
    checklistItems,
    buttonLabel,
    productTitle: modalLocale.productTitle,
    productSubtitle: modalLocale.productSubtitle,
    mascotAlt: gatorProtectLocale?.banner?.img?.alt ?? '',
  };

  return (
    <>
      <Styles.ModalCardOverride />
      <Modal
        maxWidth={800}
        maxHeight={662}
        noContentPadding
        noHeader
        onClose={onClose}
        testId={testId}
      >
        <Styles.Container>
          <Styles.Title>{content.title}</Styles.Title>
          <Styles.Subtitle>{loadingData ? <Skeleton variant="textLine" width="440px" /> : content.subtitle}</Styles.Subtitle>

          {content.message && (
            <Styles.Message>{content.message}</Styles.Message>
          )}

          <Styles.GradientCardWrapper>
            <Styles.GradientCard>
              <Styles.ProductHeader>
                <Styles.ProductTitleWrapper>
                  <Styles.ProductTitle>{content.productTitle}</Styles.ProductTitle>
                </Styles.ProductTitleWrapper>
                <Styles.ProductSubtitle>{content.productSubtitle}</Styles.ProductSubtitle>
              </Styles.ProductHeader>

              {(content.checklistItems || []).length > 0 && (
              <Styles.Checklist data-testid={`${testId}__checklist`}>
                {(content.checklistItems || []).map(item => (
                  <Styles.ChecklistItem key={item}>
                    <Styles.ChecklistIcon>
                      <IconCheck size="md" />
                    </Styles.ChecklistIcon>
                    {item}
                  </Styles.ChecklistItem>
                ))}
              </Styles.Checklist>
              )}

              <Styles.CheckboxWrapper
                onClick={() => setAgreed(v => !v)}
                aria-labelledby="active-trial-modal__checkbox-label"
                role="checkbox"
                aria-checked={agreed}
              >
                <Checkbox
                  checked={agreed}
                />

                <Styles.TermsLabel id="active-trial-modal__checkbox-label">
                  {modalLocale.termsPrefix}
                  <Link href={SERVICE_TERMS} text={modalLocale.termsLink} />
                </Styles.TermsLabel>
              </Styles.CheckboxWrapper>

              <Styles.ButtonWrapper>
                {loadingData ? (
                  <Skeleton variant="button" />
                )
                  : (
                    <Button
                      label={content.buttonLabel}
                      onClick={onActivate}
                      size="xlarge"
                      variant="primary"
                      version="v2"
                      width="fit-content"
                      loading={fastCheckoutLoading}
                      disabled={!agreed || fastCheckoutLoading}
                      testId={`${testId}__cta-activate`}
                    />
                  )
              }
              </Styles.ButtonWrapper>
            </Styles.GradientCard>

            <Styles.Mascot src={snappyMonarxPath} alt={content.mascotAlt} />
          </Styles.GradientCardWrapper>
        </Styles.Container>
      </Modal>
    </>
  );
};

ActiveTrialModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onActivate: PropTypes.func.isRequired,
  selectedPlan: PropTypes.object,
  isTrialActive: PropTypes.bool,
  isTrialExpired: PropTypes.bool,
  isAAClient: PropTypes.bool,
  monthly: PropTypes.shape({
    formattedPrice: PropTypes.string,
  }),
  loadingData: PropTypes.bool,
  fastCheckoutLoading: PropTypes.bool,
  testId: PropTypes.string,
};

export default withErrorBoundary(ActiveTrialModal, 'ActiveTrialModal');
